/**
 * ClipEditor — the alignment modal.
 *
 * Opens over the Cuts screen when the user taps a clip's waveform. Shows the
 * clip's audio WITH its surroundings; the clip's own span is the highlighted
 * region, and each side has a handle to drag in or out. The selected region
 * loops continuously while the modal is open, so alignment is done by ear:
 * drag, hear the loop change, let go when it sounds right.
 *
 * The bug that killed the previous inline version: the view window used to
 * change while the finger was down, so the x→seconds mapping moved underneath
 * the drag and one touch flew seconds away. Here the window is computed ONCE
 * when the modal opens and is frozen for the whole session — the mapping can
 * never shift mid-gesture.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from './Button';
import { previewPlayer, playPreview } from './audioBus';

export interface ClipEditorProps {
  /** The whole recording, decoded once by the page. */
  buffer: AudioBuffer;
  /** Blob URL of the original recording (for the looping player). */
  audioUrl: string;
  startSec: number;
  endSec: number;
  /** How far the neighbours may be pulled in/out, seconds. */
  reachSec?: { before: number; after: number };
  title?: string;
  saving?: boolean;
  onCancel: () => void;
  onSave: (startSec: number, endSec: number) => void;
}

const H = 150;
/** Context shown beyond the clip on each side. */
const PAD_SEC = 1.5;
/** The clip may never be shorter than this. */
const MIN_SEC = 0.1;

export function ClipEditor({
  buffer,
  audioUrl,
  startSec,
  endSec,
  reachSec,
  title,
  saving,
  onCancel,
  onSave,
}: ClipEditorProps) {
  /* FROZEN view window — the whole reason this version's drag is stable. */
  const view = useMemo<[number, number]>(() => {
    const before = Math.min(PAD_SEC, reachSec?.before ?? PAD_SEC);
    const after = Math.min(PAD_SEC, reachSec?.after ?? PAD_SEC);
    return [Math.max(0, startSec - before), Math.min(buffer.duration, endSec + after)];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [sel, setSel] = useState<[number, number]>([startSec, endSec]);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const selRef = useRef(sel);
  selRef.current = sel;

  const [lo, hi] = view;
  const span = hi - lo;
  const pct = (sec: number) => ((sec - lo) / span) * 100;

  /* ---------------- waveform (drawn once for the frozen view) -------- */

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const cssW = wrap.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(cssW * dpr));
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, H);

    const sr = buffer.sampleRate;
    const data = buffer.getChannelData(0);
    const i0 = Math.max(0, Math.floor(lo * sr));
    const i1 = Math.min(data.length, Math.ceil(hi * sr));
    const perPx = Math.max(1, Math.floor((i1 - i0) / cssW));
    let peak = 0.001;
    for (let i = i0; i < i1; i += 16) peak = Math.max(peak, Math.abs(data[i]));

    ctx.strokeStyle = '#8ea0bd';
    for (let x = 0; x < cssW; x++) {
      const s = i0 + x * perPx;
      let min = 0;
      let max = 0;
      for (let i = s; i < Math.min(s + perPx, i1); i++) {
        const v = data[i];
        if (v < min) min = v;
        if (v > max) max = v;
      }
      ctx.beginPath();
      ctx.moveTo(x + 0.5, H / 2 - (max / peak) * (H / 2 - 6));
      ctx.lineTo(x + 0.5, Math.max(H / 2 - (min / peak) * (H / 2 - 6), H / 2 - (max / peak) * (H / 2 - 6) + 1));
      ctx.stroke();
    }
  }, [buffer, lo, hi]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [draw]);

  /* ---------------- the looping selection ---------------- */

  useEffect(() => {
    // The ONE preview element, reused — never a fresh Audio per open, which
    // is how loops used to pile up and play over each other.
    //
    // UNWRITTEN OWNERSHIP PROTOCOL on the shared `previewPlayer()`.
    // Two modules drive this same element and they do NOT agree on discipline:
    //
    //   - HERE: takes it on mount (assigns `.src` unconditionally) and RELEASES
    //     it on unmount (`.src = ''`, below).
    //   - `pages/VerifyPage.tsx`: assigns only when the URL differs
    //     (`if (el.src !== audioUrl)`) and NEVER clears it.
    //
    // So whichever mounts second wins, and only this one cleans up. That is
    // safe today for exactly one reason: the two never co-mount. `App.tsx`
    // renders one page at a time (`{tab === 'cuts' && …}`), ClipEditor lives on
    // CutsPage, and the Verify card is a different tab. If a future shell ever
    // keeps pages alive across tab switches — a keep-alive shell or a router —
    // this becomes a real conflict: Verify's guard would see a stale non-empty
    // `.src`, or this unmount would blank the element Verify is mid-playback
    // on. Register both with the bus first (follow-up F1).
    const el = previewPlayer();
    el.src = audioUrl;
    el.loop = false;
    audioRef.current = el;
    let raf = 0;
    const tick = () => {
      const [a, b] = selRef.current;
      if (el.currentTime >= b || el.currentTime < a - 0.05) {
        try {
          el.currentTime = a;
        } catch {
          /* pre-metadata seek */
        }
      }
      raf = requestAnimationFrame(tick);
    };
    const start = () => {
      try {
        el.currentTime = selRef.current[0];
      } catch {
        /* ignore */
      }
      playPreview();
      raf = requestAnimationFrame(tick);
    };
    if (el.readyState >= 1) start();
    else el.addEventListener('loadedmetadata', start, { once: true });
    return () => {
      cancelAnimationFrame(raf);
      el.pause();
      el.src = '';
      audioRef.current = null;
    };
  }, [audioUrl]);

  /** Restart the loop at the new selection start whenever a drag lands. */
  const restartLoop = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    try {
      el.currentTime = selRef.current[0];
    } catch {
      /* ignore */
    }
    playPreview();
  }, []);

  /* ---------------- dragging (stable mapping) ---------------- */

  const secAt = useCallback(
    (clientX: number) => {
      const wrap = wrapRef.current;
      if (!wrap) return lo;
      const rect = wrap.getBoundingClientRect();
      const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return lo + frac * span;
    },
    [lo, span],
  );

  const onHandleDown = useCallback(
    (side: 'start' | 'end') => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      setDragging(side);
    },
    [],
  );

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const sec = secAt(e.clientX);
      setSel(([a, b]) =>
        dragging === 'start'
          ? [Math.min(Math.max(sec, lo), b - MIN_SEC), b]
          : [a, Math.max(Math.min(sec, hi), a + MIN_SEC)],
      );
    },
    [dragging, secAt, lo, hi],
  );

  const onUp = useCallback(() => {
    if (!dragging) return;
    setDragging(null);
    restartLoop();
  }, [dragging, restartLoop]);

  const nudge = useCallback(
    (side: 'start' | 'end', delta: number) => {
      setSel(([a, b]) =>
        side === 'start'
          ? [Math.min(Math.max(a + delta, lo), b - MIN_SEC), b]
          : [a, Math.max(Math.min(b + delta, hi), a + MIN_SEC)],
      );
      restartLoop();
    },
    [lo, hi, restartLoop],
  );

  const changed = Math.abs(sel[0] - startSec) > 0.01 || Math.abs(sel[1] - endSec) > 0.01;

  return (
    <div className="ce" role="dialog" aria-modal="true">
      <div className="ce__sheet">
        <div className="row ce__head">
          <strong>{title ?? 'Align the clip'}</strong>
          <span className="spacer" />
          <span className="mono ce__len">{(sel[1] - sel[0]).toFixed(2)}s</span>
        </div>

        <p className="ce__hint">Drag the edges. The highlighted part loops while you listen.</p>

        <div
          ref={wrapRef}
          className={`ce__wave${dragging ? ' is-dragging' : ''}`}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <canvas ref={canvasRef} style={{ height: H }} />
          <div className="ce__mask" style={{ left: 0, width: `${pct(sel[0])}%` }} />
          <div className="ce__mask" style={{ left: `${pct(sel[1])}%`, right: 0 }} />
          <div
            className="ce__region"
            style={{ left: `${pct(sel[0])}%`, width: `${pct(sel[1]) - pct(sel[0])}%` }}
          />
          <div
            className="ce__handle ce__handle--l"
            style={{ left: `${pct(sel[0])}%` }}
            onPointerDown={onHandleDown('start')}
          >
            <span />
          </div>
          <div
            className="ce__handle ce__handle--r"
            style={{ left: `${pct(sel[1])}%` }}
            onPointerDown={onHandleDown('end')}
          >
            <span />
          </div>
        </div>

        <div className="row ce__nudges">
          <span className="ce__nudgelabel">start</span>
          <Button size="sm" onClick={() => nudge('start', -0.05)} disabled={saving}>
            ◀
          </Button>
          <Button size="sm" onClick={() => nudge('start', 0.05)} disabled={saving}>
            ▶
          </Button>
          <span className="spacer" />
          <Button size="sm" onClick={() => nudge('end', -0.05)} disabled={saving}>
            ◀
          </Button>
          <Button size="sm" onClick={() => nudge('end', 0.05)} disabled={saving}>
            ▶
          </Button>
          <span className="ce__nudgelabel">end</span>
        </div>

        <div className="row ce__actions">
          <Button block onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            block
            variant="primary"
            onClick={() => onSave(sel[0], sel[1])}
            disabled={saving || !changed}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
